import React, { useState } from 'react';
import {
  Box, Heading, Button, Flex, useDisclosure,
  Center, Spinner, Tooltip,
  Grid, Text,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Select, Input, useToast
} from '@chakra-ui/react';
import { useSeats } from '../hooks/useSeats';
import { useEffect } from 'react';
import { EditModal } from '../components/editModal';
import { DeleteRoomModal } from '../components/deleteRoomModal';
import { GeneratePdfModal } from '../components/generatePDFModal';
import { useLocation, useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const [page, setPage] = useState(1);
  const [refreshIndex, setRefreshIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const triggerRefresh = () => setRefreshIndex((prev) => prev + 1);
  const [searchFields, setSearchFields] = useState<{ [key: string]: string }>({});
  const [selectedSeat, setSelectedSeat] = useState<any>(null);
  const [debouncedFields, setDebouncedFields] = useState(searchFields);
  const { seats } = useSeats(debouncedFields, page, refreshIndex);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isPdfOpen, onOpen: onPdfOpen, onClose: onPdfClose } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentRoom, setCurrentRoom] = useState<any>(null);

  // Bulk modification states
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedSeats, setSelectedSeats] = useState<Set<string>>(new Set());
  const { isOpen: isBulkModalOpen, onOpen: onBulkModalOpen, onClose: onBulkModalClose } = useDisclosure();
  const [bulkData, setBulkData] = useState({
    stato: '',
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    prezzo: ''
  });
  const toast = useToast();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedFields(searchFields);
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchFields]);

  useEffect(() => {
    const stateRoom = (location.state as any)?.room;
    if (stateRoom) {
      setCurrentRoom(stateRoom);
      setSearchFields({
        ...searchFields,
        luogo: stateRoom.luogo,
        data: stateRoom.data
      });
      setPage(1);
      triggerRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const onEdit = (seat: any) => {
    setSelectedSeat(seat);
    onOpen();
  };

  const handleDeleteSuccess = () => {
    navigate('/rooms');
  };

  const toggleBulkMode = () => {
    setBulkMode(!bulkMode);
    setSelectedSeats(new Set());
  };

  const toggleSeatSelection = (seatId: string) => {
    const newSelected = new Set(selectedSeats);
    if (newSelected.has(seatId)) {
      newSelected.delete(seatId);
    } else {
      newSelected.add(seatId);
    }
    setSelectedSeats(newSelected);
  };

  const handleBulkEdit = () => {
    if (selectedSeats.size === 0) {
      toast({
        title: "Nessun posto selezionato",
        description: "Seleziona almeno un posto per la modifica multipla",
        status: "warning",
        duration: 3000,
        isClosable: true
      });
      return;
    }
    onBulkModalOpen();
  };

  const handleBulkSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const seatIds = Array.from(selectedSeats);

      // Prepara i dati da inviare (solo i campi compilati)
      const updateData: any = {};
      if (bulkData.stato) updateData.stato = bulkData.stato;
      if (bulkData.nome) updateData.nome = bulkData.nome;
      if (bulkData.cognome) updateData.cognome = bulkData.cognome;
      if (bulkData.email) updateData.email = bulkData.email;
      if (bulkData.telefono) updateData.telefono = bulkData.telefono;
      if (bulkData.prezzo) updateData.prezzo = parseFloat(bulkData.prezzo);

      // Effettua le richieste di aggiornamento per ogni posto
      const promises = seatIds.map(seatId =>
        fetch(`${process.env.REACT_APP_API_URL}/seat/${seatId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(updateData)
        })
      );

      await Promise.all(promises);

      toast({
        title: "Aggiornamento completato",
        description: `${seatIds.length} posti aggiornati con successo`,
        status: "success",
        duration: 3000,
        isClosable: true
      });

      onBulkModalClose();
      setBulkMode(false);
      setSelectedSeats(new Set());
      setBulkData({ stato: '', nome: '', cognome: '', email: '', telefono: '', prezzo: '' });
      triggerRefresh();
    } catch (err) {
      console.error(err);
      toast({
        title: "Errore",
        description: "Errore durante l'aggiornamento dei posti",
        status: "error",
        duration: 3000,
        isClosable: true
      });
    }
  };

  // Calcola le dimensioni della griglia in base ai posti esistenti
  const { maxRow, maxCol, seatMap } = React.useMemo(() => {
    const map = new Map<string, any>();
    let maxR = 0;
    let maxC = 0;

    (seats || []).forEach((s: any) => {
      const fila = String(s.fila).toUpperCase();
      const numero = s.numero;
      const label = `${fila}${numero}`;
      map.set(label, s);

      const rowIndex = fila.charCodeAt(0) - 65;
      if (rowIndex > maxR) maxR = rowIndex;
      if (numero > maxC) maxC = numero;
    });

    return { maxRow: maxR, maxCol: maxC, seatMap: map };
  }, [seats]);

  const getColor = (seat: any, isSelected: boolean) => {
    if (isSelected) return 'purple.400'; // Evidenzia i posti selezionati
    if (!seat) return 'gray.200';
    return seat.stato === 'Disponibile' ? 'green.400'
      : seat.stato === 'Prenotato' ? 'red.400'
        : seat.stato === 'Disabili' ? 'blue.400'
          : 'gray.300';
  };

  const handleTileClick = (seat: any) => {
    if (!seat) return;

    if (bulkMode) {
      toggleSeatSelection(seat._id);
    } else {
      onEdit(seat);
    }
  };

  if (!seats) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (seats.length === 0) {
    return (
      <Center minH="60vh">
        <Text>Nessun posto trovato per questa sala</Text>
      </Center>
    );
  }

  const ROWS = Array.from({ length: maxRow + 1 }, (_, i) => String.fromCharCode(65 + i));
  const COLS = maxCol;

  return (
    <Box p={6}>
      <Flex
        justify={{ base: "center", md: "left" }}
        align="center"
        mb={4}
        gap={{ base: 4, md: 10 }}
        direction={{ base: "column", md: "row" }}
        flexWrap="wrap"
      >
        <Heading mb={{ base: 2, md: 0 }}>Seat Dashboard</Heading>

        <Button
          colorScheme={bulkMode ? "purple" : "teal"}
          onClick={toggleBulkMode}
        >
          {bulkMode ? "Annulla Selezione" : "Modifica Multipla"}
        </Button>

        {bulkMode && (
          <Button colorScheme="purple" onClick={handleBulkEdit}>
            Modifica Selezionati ({selectedSeats.size})
          </Button>
        )}

        {!bulkMode && (
          <>
            <Button colorScheme="red" onClick={onDeleteOpen}>
              Elimina Sala
            </Button>
            <Button colorScheme="blue" onClick={onPdfOpen}>
              Genera PDF Prenotati
            </Button>
          </>
        )}

        <Button colorScheme="yellow" onClick={() => navigate('/rooms')}>
          ← Tutte le sale
        </Button>
      </Flex>

      {bulkMode && (
        <Box mb={4} p={3} bg="purple.100" borderRadius="md">
          <Text fontWeight="bold" color="purple.800">
            Modalità Selezione Multipla Attiva
          </Text>
          <Text fontSize="sm" color="purple.700">
            Clicca sui posti per selezionarli, poi premi "Modifica Selezionati"
          </Text>
        </Box>
      )}

      {currentRoom && (
        <Box mb={4} p={4} bg="gray.100" borderRadius="md">
          <Text fontWeight="bold">{currentRoom.titoloEvento || 'Sala'}</Text>
          <Text fontSize="sm">Luogo: {currentRoom.luogo} - Data: {new Date(currentRoom.data).toLocaleDateString('it-IT')}</Text>
        </Box>
      )}

      <EditModal seat={selectedSeat} isOpen={isOpen} onClose={onClose} onSave={triggerRefresh} />
      <DeleteRoomModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onSuccess={handleDeleteSuccess}
        currentRoom={currentRoom}
      />
      <GeneratePdfModal
        isOpen={isPdfOpen}
        onClose={onPdfClose}
        currentRoom={currentRoom}
      />

      {/* Bulk Edit Modal */}
      <Modal isOpen={isBulkModalOpen} onClose={onBulkModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Modifica Multipla ({selectedSeats.size} posti)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} fontSize="sm" color="gray.600">
              Compila solo i campi che vuoi modificare. I campi vuoti non verranno modificati.
            </Text>

            <FormControl mb={3}>
              <FormLabel>Stato</FormLabel>
              <Select
                value={bulkData.stato}
                onChange={(e) => setBulkData({ ...bulkData, stato: e.target.value })}
                placeholder="Non modificare"
              >
                <option value="Disponibile">Disponibile</option>
                <option value="Prenotato">Prenotato</option>
                <option value="Disabili">Disabili</option>
                <option value="Vuoto">Vuoto</option>
              </Select>
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Nome</FormLabel>
              <Input
                value={bulkData.nome}
                onChange={(e) => setBulkData({ ...bulkData, nome: e.target.value })}
                placeholder="Lascia vuoto per non modificare"
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Cognome</FormLabel>
              <Input
                value={bulkData.cognome}
                onChange={(e) => setBulkData({ ...bulkData, cognome: e.target.value })}
                placeholder="Lascia vuoto per non modificare"
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={bulkData.email}
                onChange={(e) => setBulkData({ ...bulkData, email: e.target.value })}
                placeholder="Lascia vuoto per non modificare"
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Telefono</FormLabel>
              <Input
                value={bulkData.telefono}
                onChange={(e) => setBulkData({ ...bulkData, telefono: e.target.value })}
                placeholder="Lascia vuoto per non modificare"
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel>Prezzo</FormLabel>
              <Input
                type="number"
                value={bulkData.prezzo}
                onChange={(e) => setBulkData({ ...bulkData, prezzo: e.target.value })}
                placeholder="Lascia vuoto per non modificare"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" mr={3} onClick={handleBulkSave}>
              Salva Modifiche
            </Button>
            <Button variant="ghost" onClick={onBulkModalClose}>Annulla</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box overflowX="auto" w="100%" py={2}>
        <Grid
          as="section"
          gap={{ base: 1, md: 2 }}
          justifyContent="start"
          templateColumns={{
            base: `repeat(${COLS}, 30px)`,
            md: `repeat(${COLS}, 40px)`,
            lg: `repeat(${COLS}, 44px)`
          }}
          px={2}
        >
          {ROWS.map((row) =>
            Array.from({ length: COLS }, (_, colIdx) => {
              const colNum = colIdx + 1;
              const label = `${row}${colNum}`;
              const seat = seatMap.get(label);
              const isSelected = seat && selectedSeats.has(seat._id);
              const bg = getColor(seat, isSelected);

              return (
                <Tooltip key={label} label={seat ? `${label} - ${seat.stato}` : `${label} - Non disponibile`} hasArrow>
                  <Button
                    onClick={() => handleTileClick(seat)}
                    bg={bg}
                    color="black"
                    w={{ base: '24px', md: '36px', lg: '44px' }}
                    h={{ base: '24px', md: '36px', lg: '44px' }}
                    minW={{ base: '24px', md: '36px', lg: '44px' }}
                    minH={{ base: '24px', md: '36px', lg: '44px' }}
                    fontSize={{ base: '10px', md: '11px', lg: '12px' }}
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    _hover={{ opacity: 0.8 }}
                    borderRadius="6px"
                    cursor={seat ? 'pointer' : 'not-allowed'}
                    isDisabled={!seat}
                    border={isSelected ? '2px solid purple' : 'none'}
                  >
                    {label}
                  </Button>
                </Tooltip>
              );
            })
          )}
        </Grid>
      </Box>

      <Box mt={4} p={4} bg="gray.50" borderRadius="md">
        <Flex gap={6} flexWrap="wrap">
          <Flex align="center" gap={2}>
            <Box w="20px" h="20px" bg="green.400" borderRadius="md" />
            <Text fontSize="sm">Disponibile</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <Box w="20px" h="20px" bg="red.400" borderRadius="md" />
            <Text fontSize="sm">Prenotato</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <Box w="20px" h="20px" bg="blue.400" borderRadius="md" />
            <Text fontSize="sm">Disabili</Text>
          </Flex>
          <Flex align="center" gap={2}>
            <Box w="20px" h="20px" bg="gray.200" borderRadius="md" />
            <Text fontSize="sm">Non disponibile</Text>
          </Flex>
          {bulkMode && (
            <Flex align="center" gap={2}>
              <Box w="20px" h="20px" bg="purple.400" borderRadius="md" border="2px solid purple" />
              <Text fontSize="sm">Selezionato</Text>
            </Flex>
          )}
        </Flex>
      </Box>
    </Box>
  );
};

export default Dashboard;